import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AFFILIATE_PAGE_SOURCE, trackPrimaryCtaClick } from '../../utils/enhancedAnalytics';
import { usePageSeo } from '../../seo/usePageSeo';
import {
  getCompatibilityLandingChoices,
  getCompatibilityPathForPair,
} from './compatibilityPageConfigs';
import './CompatibilityLanding.css';

export const COMPATIBILITY_LANDING_PATH = '/compatibility';
export const COMPATIBILITY_LANDING_TITLE = 'Check LiveScope Compatibility | Angler Built';
export const COMPATIBILITY_LANDING_META_DESCRIPTION =
  'Check whether your Garmin display and LiveScope transducer work together, including required hardware and compatibility guidance from Angler Built.';

export default function CompatibilityLanding() {
  const { displays, livescopes } = useMemo(
    () => getCompatibilityLandingChoices(),
    []
  );
  const [displaySlug, setDisplaySlug] = useState('');
  const [livescopeSlug, setLivescopeSlug] = useState('');
  const resultPath = getCompatibilityPathForPair(displaySlug, livescopeSlug);
  const bothSelected = Boolean(displaySlug && livescopeSlug);

  usePageSeo({
    title: COMPATIBILITY_LANDING_TITLE,
    description: COMPATIBILITY_LANDING_META_DESCRIPTION,
    path: COMPATIBILITY_LANDING_PATH,
  });

  return (
    <div className="ab-compat-landing">
      <header className="ab-compat-landing-hero">
        <p className="ab-compat-landing-kicker">Angler Built compatibility</p>
        <h1 className="ab-compat-landing-title">Check LiveScope Compatibility</h1>
        <p className="ab-compat-landing-lede">
          Select the Garmin display you already own and the LiveScope you are
          considering. Angler Built will show whether they work together and what
          the setup requires.
        </p>
      </header>

      <form
        className="ab-compat-landing-form"
        onSubmit={(event) => event.preventDefault()}
      >
        <fieldset className="ab-compat-landing-fieldset">
          <legend className="ab-compat-landing-legend">
            What Garmin display do you have?
          </legend>
          <div className="ab-compat-landing-choices" role="group">
            {displays.map((display) => {
              const selected = display.slug === displaySlug;
              return (
                <button
                  key={display.slug}
                  type="button"
                  className={`ab-compat-landing-choice${
                    selected ? ' is-selected' : ''
                  }`}
                  aria-pressed={selected}
                  onClick={() => setDisplaySlug(display.slug)}
                >
                  {display.label}
                </button>
              );
            })}
          </div>
        </fieldset>

        <fieldset className="ab-compat-landing-fieldset">
          <legend className="ab-compat-landing-legend">
            Which LiveScope are you considering?
          </legend>
          <div className="ab-compat-landing-choices" role="group">
            {livescopes.map((livescope) => {
              const selected = livescope.slug === livescopeSlug;
              return (
                <button
                  key={livescope.slug}
                  type="button"
                  className={`ab-compat-landing-choice${
                    selected ? ' is-selected' : ''
                  }`}
                  aria-pressed={selected}
                  onClick={() => setLivescopeSlug(livescope.slug)}
                >
                  {livescope.label}
                </button>
              );
            })}
          </div>
        </fieldset>

        <div className="ab-compat-landing-action">
          {resultPath ? (
            <Link
              className="ab-compat-landing-submit"
              to={resultPath}
              data-testid="compat-landing-submit"
              onClick={() => {
                trackPrimaryCtaClick({
                  pageSource: AFFILIATE_PAGE_SOURCE.COMPATIBILITY_PAGE,
                  placement: 'compatibility_landing',
                  cta: 'see_compatibility',
                });
              }}
            >
              See compatibility
            </Link>
          ) : (
            <button
              type="button"
              className="ab-compat-landing-submit is-disabled"
              disabled
              data-testid="compat-landing-submit"
            >
              See compatibility
            </button>
          )}
          {bothSelected && !resultPath ? (
            <p className="ab-compat-landing-unavailable" role="status">
              That combination is not available to check yet.
            </p>
          ) : null}
        </div>
      </form>
    </div>
  );
}
